import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Plus, Save, X, Copy, ExternalLink } from 'lucide-react';
import { specialEventAPI, trekAPI, contentAPI, adminAPI } from '../services/api';
import { specialEventPublicUrl } from '../utils/specialEventPublicUrl.js';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useListQueryParams from '../hooks/useListQueryParams';
import { PageHeader, FilterBar, EmptyState, StatusChip } from '../components/ui';
import MultiImageUpload from '../components/MultiImageUpload';
import './ModuleAdmin.css';

const emptyForm = {
  name: '',
  tagline: '',
  slug: '',
  status: 'active',
  videoId: '',
  isHighlighted: false,
  isActive: true
};

import { useNavigate } from 'react-router-dom';

const SpecialEvents = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { query, setQuery } = useListQueryParams({ page: '1', limit: '20', search: '', status: '' });
  const page = Number(query.page || 1);
  const limit = Number(query.limit || 20);
  const search = query.search || '';
  const status = query.status || '';
  const { permissions } = useAuth();
  const { showToast } = useToast();
  const canManageSpecialEvents =
    permissions.includes('*') || permissions.includes('special_events_manage');
  const canEditSlugs = permissions.includes('*') || permissions.includes('slug_manage');

  const { data, isLoading } = useQuery(
    ['special-events', page, limit, search, status],
    () => specialEventAPI.list({ page, limit, search: search || undefined, status: status || undefined })
  );
  const items = data?.data?.data || [];
  const pagination = data?.data?.pagination || { page: 1, totalPages: 1, total: items.length };


  const deleteMutation = useMutation((id) => specialEventAPI.remove(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['special-events']);
    }
  });

  const onEdit = (item) => {
    navigate(`/special-events/${item.id}/edit`);
  };

  const copyShareUrl = async (url) => {
    const target = String(url || '').trim();
    if (!target) return;
    try {
      await navigator.clipboard.writeText(target);
      showToast({ message: 'Link copied to clipboard.', severity: 'success' });
    } catch {
      showToast({ message: 'Could not copy link.', severity: 'error' });
    }
  };

  return (
    <div className="module-page special-events-page">
      <PageHeader
        title="Special Events"
        subtitle="Manage special event collections, enabled treks and stories."
        actions={
          canManageSpecialEvents ? (
            <button
              className="special-events-btn-primary"
              type="button"
              onClick={() => navigate('/special-events/new')}
            >
              <Plus size={16} /> Add Special Event
            </button>
          ) : null
        }
      />

      <FilterBar>
        <input
          type="text"
          className="module-filter-input"
          placeholder="Search special events..."
          value={search}
          onChange={(e) => setQuery({ search: e.target.value })}
        />
        <select className="module-filter-select" value={status} onChange={(e) => setQuery({ status: e.target.value })}>
          <option value="">All status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="draft">Draft</option>
        </select>
      </FilterBar>

      <div className="module-card">
        {isLoading ? (
          <p className="module-loading">Loading special events...</p>
        ) : items.length === 0 ? (
          <EmptyState
            title="No special events yet"
            description="Create collections with enabled treks and stories."
          />
        ) : (
          <>
          <table className="module-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Public link</th>
                <th>Status</th>
                <th>Highlighted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.slug}</td>
                  <td>
                    {item.slug ? (
                      <div className="special-events-table-link">
                        <a
                          href={specialEventPublicUrl(item.slug)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="module-link-btn"
                        >
                          View
                        </a>
                        <button
                          type="button"
                          className="module-link-btn"
                          onClick={() => copyShareUrl(specialEventPublicUrl(item.slug))}
                        >
                          Copy
                        </button>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td><StatusChip status={item.status || 'default'} label={item.status || '—'} /></td>
                  <td>{item.isHighlighted ? <StatusChip status="active" label="Yes" /> : <StatusChip status="default" label="No" />}</td>
                  <td className="module-actions">
                    <button className="module-link-btn" onClick={() => onEdit(item)}>
                      Edit
                    </button>
                    <button className="module-link-btn danger" onClick={() => { if (window.confirm('Delete special event?')) deleteMutation.mutate(item.id); }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pagination.totalPages > 1 && (
            <div className="module-list-pagination">
              <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)</span>
              <div className="module-list-pagination-btns">
                <button type="button" className="module-btn secondary" disabled={page <= 1} onClick={() => setQuery({ page: String(page - 1) }, { resetPage: false })}>Previous</button>
                <button type="button" className="module-btn secondary" disabled={page >= pagination.totalPages} onClick={() => setQuery({ page: String(page + 1) }, { resetPage: false })}>Next</button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default SpecialEvents;
